import {
    AutocompleteInput,
    Create,
    DateInput,
    ReferenceInput,
    SimpleForm,
    TextInput
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import {
    PackagePlus
} from 'lucide-react';
import { useGetOne, useNotify, useRedirect } from 'ra-core';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { AutoReferenceField } from '../documents/shared/AutoReferenceField';
import { StatusSelectInput } from '../documents/shared/StatusSelectInput';

/**
 * POItemsPreview — reads the linked PO and displays imported line items
 * so the user can verify what they are receiving.
 */
const POItemsPreview = ({ poId }: { poId: string | number }) => {
    const { data: po } = useGetOne<any>('purchase_orders', { id: poId });
    if (!po?.items?.length) return null;

    return (
        <div className="md:col-span-2 mt-4">
            <div className="flex items-center gap-3 mb-3">
                <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Items from PO: {po.reference}
                </h4>
                <div className="flex-1 h-px bg-slate-200" />
                <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                    {po.items.length} items
                </Badge>
            </div>
            <div className="border rounded-xl overflow-hidden bg-card shadow-sm">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-slate-800 text-white">
                            <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider">#</th>
                            <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider">Description</th>
                            <th className="px-4 py-2.5 text-center text-[11px] uppercase tracking-wider">HSN</th>
                            <th className="px-4 py-2.5 text-center text-[11px] uppercase tracking-wider">Ordered Qty</th>
                            <th className="px-4 py-2.5 text-center text-[11px] uppercase tracking-wider">UoM</th>
                        </tr>
                    </thead>
                    <tbody>
                        {po.items.map((item: any, i: number) => (
                            <tr key={i} className="border-t hover:bg-muted transition-colors">
                                <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{i + 1}</td>
                                <td className="px-4 py-3 font-medium text-foreground">{item.description}</td>
                                <td className="px-4 py-3 text-center font-mono text-xs text-muted-foreground">{item.hsn_code || '—'}</td>
                                <td className="px-4 py-3 text-center font-bold text-foreground">{item.quantity}</td>
                                <td className="px-4 py-3 text-center text-muted-foreground text-xs">{item.unit || 'Nos'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="bg-blue-50 px-4 py-2 text-xs text-blue-600 font-medium border-t border-blue-100">
                    💡 After saving the GRN, open the receipt to mark individual items as accepted/rejected under the Items &amp; QA tab.
                </div>
            </div>
        </div>
    );
};

/** Watches po_id field and shows item preview reactively */
const POWatcher = ({ defaultPoId }: { defaultPoId?: string | number }) => {
    const { watch } = useFormContext();
    const poId = watch('po_id') || defaultPoId;
    if (!poId) return null;
    return <POItemsPreview poId={poId} />;
};

export const GRNCreate = () => {
    const location = useLocation();
    const stateRecord = location.state?.record || {};
    const notify = useNotify();
    const redirect = useRedirect();

    // grn_items rows are seeded from the linked PO's line items by the
    // `trigger_auto_grn_items` DB trigger (fires on goods_receipts insert),
    // so the "Items & QA" tab has rows to accept/reject without duplicating
    // that insert here.
    const onSuccess = async (data: any) => {
        notify('ra.notification.created', { type: 'info', messageArgs: { smart_count: 1 } });
        redirect(`/goods_receipts/${data.id}/show`);
    };

    return (
        <Create title="Record Goods Receipt (GRN)" mutationOptions={{ onSuccess }}>
            <SimpleForm defaultValues={stateRecord} className="max-w-4xl">
                {/* Header */}
                <div className="mb-6 flex items-center gap-3 text-foreground pb-4 border-b">
                    <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center border border-emerald-100">
                        <PackagePlus className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold">New Goods Receipt Note (GRN)</h3>
                        <p className="text-xs text-muted-foreground">Record goods received against a Purchase Order</p>
                    </div>
                </div>

                {/* Reference + Date row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-4">
                    <AutoReferenceField docType="goods_receipt" />
                    <DateInput source="received_date" label="Received Date" required />
                </div>

                {/* PO + Vendor row — read-only if pre-filled from PO (a truly
                    disabled RHF field never submits its value, silently
                    dropping po_id/vendor_id on save) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-4">
                    <ReferenceInput source="po_id" reference="purchase_orders" perPage={100} sort={{ field: 'date', order: 'DESC' }}>
                        <AutocompleteInput
                            label="Purchase Order"
                            optionText="reference"
                            isRequired
                            readOnly={!!stateRecord.po_id}
                        />
                    </ReferenceInput>

                    <ReferenceInput source="vendor_id" reference="vendors" perPage={100}>
                        <AutocompleteInput
                            label="Vendor"
                            optionText="name"
                            isRequired
                            readOnly={!!stateRecord.vendor_id}
                        />
                    </ReferenceInput>
                </div>

                {/* Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-4">
                    <StatusSelectInput
                        source="status"
                        isRequired
                        defaultValue="draft"
                        choices={[
                            { id: 'draft',     name: 'Draft — pending verification' },
                            { id: 'received',  name: 'Received — goods on hand' },
                            { id: 'qa_passed', name: 'QA Passed — ready for billing' },
                            { id: 'qa_failed', name: 'QA Failed — return/rework needed' },
                        ]}
                    />
                    <TextInput source="notes" label="Notes / Remarks" multiline rows={3} />
                </div>

                {/* PO items preview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                    <POWatcher defaultPoId={stateRecord.po_id} />
                </div>
            </SimpleForm>
        </Create>
    );
};
